import { configureApiClient } from "@healthos/api-client";
import {
  AuthProvider,
  configureAuth,
  configureHostAuth,
  configureImpersonation,
  configureIsInMsTeamsAuthWrapper,
  createHostAuthSession,
} from "@healthos/auth/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { NuqsAdapter } from "nuqs/adapters/react-router/v7";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { App } from "./app";
import { RootErrorBoundary } from "./components/root-error-boundary";
import { readEnv } from "./env";
import { initBranch } from "./lib/branch";
import { buildInfo, publishBuildInfo } from "./lib/build-info";
import { initDatadogRum } from "./lib/datadog";
import { applyEmailAttribution } from "./lib/email-attribution";
import { authedFetch } from "./lib/fetch-with-auth";
import { initGlobalErrorReporting } from "./lib/global-error-reporting";
import {
  createDesktopErrorSubscriber,
  createDesktopLogoutRequester,
  createDesktopReloadSubscriber,
  createDesktopTokenRequester,
  createDesktopVersionResponder,
  initHostAuth,
  isTeamsParentOrigin,
} from "./lib/host-auth";
import { createImpersonationTokenRequester, initImpersonation } from "./lib/impersonation-auth";

/*
 * Self-hosted variable fonts. Replaces the Google Fonts <link> chain
 * in index.html that cost ~300-500ms in DNS+TLS to fonts.googleapis +
 * fonts.gstatic on every cold load. Variable axes mean one woff2 per
 * family covers all weights we use, so the network footprint is tiny.
 * Font-display: swap is the @fontsource default, so first paint never
 * waits on a font byte.
 */
import "@fontsource-variable/inter";
import "@fontsource-variable/inter-tight";
import "@fontsource-variable/newsreader/opsz.css";
import "@fontsource-variable/newsreader/opsz-italic.css";
import "@fontsource-variable/playfair-display";
import "@fontsource-variable/playfair-display/wght-italic.css";
import "@fontsource-variable/jetbrains-mono";
// Poppins is the dark theme's heading face, matching mobile. @font-face only
// downloads when a rendered element uses it, so light-theme members never fetch it.
import "@fontsource/poppins/400.css";
import "@fontsource/poppins/500.css";
import "@fontsource/poppins/500-italic.css";
import "@fontsource/poppins/600.css";
import "./styles.css";
import "./styles/onboarding.css";
import "./styles/ops.css";

/*
 * Single QueryClient for the SPA. Defaults bias toward "data is fresh
 * unless told otherwise":
 *   - 30s staleTime so back-button + tab-switch don't refetch every read
 *   - retry once on transient network blips (auth flows handle their own
 *     401s, so retry only helps with TCP/DNS hiccups)
 *   - refetchOnWindowFocus disabled, the editorial register is "quiet
 *     unless something changed"; the User-DO Heartbeat will push real
 *     invalidations over SSE.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
    mutations: { retry: 0 },
  },
});

const env = readEnv();

// Expose release provenance (version, commit, build time) on
// window.__HEALTHOS__ so a released browser build is self-identifying.
publishBuildInfo();

// Installed first, before anything else can throw: catches errors and
// unhandled promise rejections RootErrorBoundary can't see (see
// global-error-reporting.ts for why that gap exists).
initGlobalErrorReporting();

/*
 * Boot is async because the impersonation receiver (G-imp, below) may need to
 * redeem a one-time code before anything else can decide how to configure
 * auth. `initImpersonation` never rejects (see its doc comment), so this
 * never leaves the app unrendered behind an unhandled rejection.
 */
async function boot(): Promise<void> {
  /*
   * Wire the auth-bearing fetch impl before anything that might call
   * `apiFetch`/`absoluteApiUrl` - including the impersonation redeem just
   * below, which hits core-api directly. `authedFetch` and `env.apiBaseUrl`
   * are both resolved at module scope, so this has no dependency on
   * anything computed later in boot() and is safe to run first. Without
   * this ordering, `initImpersonation()`'s redeem call reads the
   * unconfigured default client (empty baseUrl) and 404s against the SPA's
   * own origin instead of core-api, in every environment, not just locally.
   */
  configureApiClient({
    fetch: authedFetch,
    baseUrl: env.apiBaseUrl,
  });

  /*
   * Impersonation receiver (G-imp). A no-op until a `#impersonate=<code>`
   * fragment is present; when it is, the code has already been redeemed
   * into a real token by this point. The real gates live server-side
   * (OPERATOR_JWT_ISSUER + IMPERSONATION_TOKEN_SECRET), since a code here
   * with no valid backend session behind it is inert either way.
   */
  const impersonationToken = await initImpersonation();

  /*
   * Embedded host-auth gate (G1). Only when the desktop shell loads us with
   * ?embed=desktop and we sit inside an allowlisted Tauri origin do we switch the
   * AuthProvider to its host-auth session source and reflect the host in the
   * platform hook. Absent either signal this is a no-op and the standalone web
   * app behaves byte-for-byte as before. Resolved before Datadog init so RUM,
   * logs, and session replay can be tagged with the originating client.
   *
   * Skipped entirely when impersonation activated above: the two are
   * mutually exclusive delivery contexts (a load is never both a desktop/
   * Teams embed and an admin-console impersonation redirect), and
   * `configureHostAuth` below already installs the impersonation session in
   * that case.
   */
  let hostAuth: ReturnType<typeof initHostAuth> = null;
  let isTeamsWrapper = false;
  if (impersonationToken) {
    configureHostAuth(
      createHostAuthSession({
        requestToken: createImpersonationTokenRequester(impersonationToken),
      }),
    );
    configureImpersonation(true, impersonationToken.profile, impersonationToken.expiresAt);
  } else {
    hostAuth = initHostAuth();
    isTeamsWrapper = isTeamsParentOrigin(hostAuth?.parentOrigin ?? null);
    if (hostAuth) {
      window.__HEALTHOS_HOST__ = isTeamsWrapper ? "teams" : "desktop";
    }
    configureIsInMsTeamsAuthWrapper(isTeamsWrapper);
  }

  initDatadogRum({
    applicationId: env.ddApplicationId,
    clientToken: env.ddClientToken,
    env: env.ddEnv,
    apiBaseUrl: env.apiBaseUrl,
    client: impersonationToken
      ? "healthos-impersonation"
      : hostAuth
        ? isTeamsWrapper
          ? "healthos-teams"
          : "healthos-desktop"
        : undefined,
  });

  // Immediately after RUM init and before routing, so a member arriving from a
  // lifecycle email has the campaign on their session for every event that follows,
  // including the first view. Reading it later would miss that view; the OIDC
  // redirect can also drop the query string, so the earliest read is the only
  // reliable one. A no-op on an ordinary visit.
  applyEmailAttribution();

  /*
   * Branch attribution session. Started here, and not awaited, for the same
   * reason as the line above: a visit referred by a Branch link carries its
   * deep-link destination in the query string, and the OIDC redirect can drop
   * that, so the read has to happen before routing gets a chance to move. It
   * is fire-and-forget because nothing on the critical path needs the result:
   * `useBranchDeepLink` picks up the resolved destination once the router is
   * mounted, and `initBranch` never rejects. A no-op without VITE_BRANCH_KEY,
   * which is every local and test run.
   */
  void initBranch(env.branchKey);

  if (env.configured && env.authority && env.clientId) {
    configureAuth({
      authority: env.authority,
      clientId: env.clientId,
      redirectUri: env.redirectUri,
      postLogoutRedirectUri: env.postLogoutRedirectUri,
      silentRedirectUri: env.silentRedirectUri,
      scope: env.scope,
      provider: env.provider,
    });
  }

  if (hostAuth) {
    configureHostAuth(
      createHostAuthSession({
        requestToken: createDesktopTokenRequester(hostAuth),
        subscribe: createDesktopErrorSubscriber(hostAuth),
        requestLogout: createDesktopLogoutRequester(hostAuth),
      }),
    );
    createDesktopReloadSubscriber(hostAuth);
    // Only the desktop shell surfaces the embed's build in its menu; the Teams
    // wrapper neither asks for nor displays it.
    if (!isTeamsWrapper) {
      createDesktopVersionResponder(hostAuth, buildInfo);
    }
  }

  // biome-ignore lint/style/noNonNullAssertion: #root is guaranteed by index.html
  const root = createRoot(document.getElementById("root")!);
  root.render(
    <StrictMode>
      {/*
        Outermost boundary: AmbiencePlayerProvider, BootProvider, and
        AuthProvider all render above the boundary App() mounts internally
        (see RootErrorBoundary's placement in app.tsx), so a throw from any
        of them - or from QueryClientProvider/BrowserRouter/NuqsAdapter -
        previously had nothing to catch it and unmounted straight to a blank
        #root instead of this recovery card.
      */}
      <RootErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <NuqsAdapter>
              <AuthProvider>
                <App />
              </AuthProvider>
            </NuqsAdapter>
          </BrowserRouter>
        </QueryClientProvider>
      </RootErrorBoundary>
    </StrictMode>,
  );
}

void boot();
